import { ApiGroups } from "@/react/features/vcc/types/agents";

import { COLUMN_DATA } from "./columnDefinitions";
import NodeState from "./types/nodeState";
import flattenTree from "./flattenTree";

const columnData = (key) => {
  const column = COLUMN_DATA.find((item) => item.id === key);
  return {
    id: column.id,
    displayName: column.header,
  };
};

/**
 * This tree models the relation between the {@link ApiGroups} and the
 * table columns in {@link COLUMN_DATA}.
 */
export default Object.freeze(
  flattenTree([
    {
      ...columnData("id"),
      nodeState: NodeState.CHECKED,
    },
    {
      ...columnData("roles"),
      nodeState: NodeState.CHECKED,
    },
    {
      id: ApiGroups.SSO,
      displayName: "SSO",
      isGroup: true,
      nodeState: NodeState.CHECKED,
      children: [
        {
          ...columnData("sso.sso_id"),
          nodeState: NodeState.CHECKED,
        },
        {
          ...columnData("sso.sso_username"),
          nodeState: NodeState.CHECKED,
        },
      ],
    },
    {
      id: ApiGroups.PROFILE,
      displayName: "Profile",
      isGroup: true,
      nodeState: NodeState.CHECKED,
      children: [
        {
          ...columnData("profile.first_name"),
          nodeState: NodeState.CHECKED,
        },
        {
          ...columnData("profile.last_name"),
          nodeState: NodeState.CHECKED,
        },
        {
          ...columnData("profile.display_name"),
          nodeState: NodeState.CHECKED,
        },
        {
          ...columnData("profile.email"),
          nodeState: NodeState.CHECKED,
        },
        {
          ...columnData("profile.comment"),
          nodeState: NodeState.CHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT,
      displayName: "Agent - General",
      isGroup: true,
      nodeState: NodeState.CHECKED,
      children: [
        {
          ...columnData("agent.legacy_guid"),
          nodeState: NodeState.CHECKED,
        },
        {
          ...columnData("agent.legacy_uid"),
          nodeState: NodeState.CHECKED,
        },
        {
          ...columnData("agent.group_id"),
          nodeState: NodeState.CHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_CHAT,
      displayName: "Agent - Chat",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.chat.concurrent_limit"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.chat.agent_to_agent_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_CRM,
      displayName: "Agent - CRM",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.crm.hide_customer_contact_info_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_EMAIL,
      displayName: "Agent - Email",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.email.pull_emails_from_queue_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.email.delete_pending_emails_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.email.default_to_channel_from_address_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.email.default_from_address"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.email.signature"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.email.default_signature"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_SMTP,
      displayName: "Agent - SMTP",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.smtp.server_id"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.smtp.override_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_INTERACTION,
      displayName: "Agent - Interaction",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.interactions.offer_timeout"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.interactions.offer_rejection_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.interactions.primary_language"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_EXTERNAL_CRM,
      displayName: "Agent - External CRM",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.external_crm.username"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.external_crm.password"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.external_crm.salesforce_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_SUPERVISOR,
      displayName: "Agent - Supervisor",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.supervisor.enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.supervisor.call_monitoring_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.supervisor.report_restriction_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
    {
      id: ApiGroups.AGENT_PHONE,
      displayName: "Agent - Phone",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.phone.phone_type"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.pstn_number"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.pstn_number_change_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.ip_uri"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.ip_uri_change_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.phone_settings_validated"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.auto_answer_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.pbx_number"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.connection_mode"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.connection_mode_change_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.extension_number"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.phone.extension_type"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          id: ApiGroups.AGENT_PHONE_DAR,
          displayName: "DAR",
          isGroup: true,
          nodeState: NodeState.UNCHECKED,
          children: [
            {
              ...columnData("agent.phone.dar_enabled"),
              nodeState: NodeState.UNCHECKED,
            },
            {
              ...columnData("agent.phone.dar_voicemail_enabled"),
              nodeState: NodeState.UNCHECKED,
            },
          ],
        },
        {
          id: ApiGroups.AGENT_PHONE_OUTBOUND,
          displayName: "Outbound",
          isGroup: true,
          nodeState: NodeState.UNCHECKED,
          children: [
            {
              ...columnData("agent.phone.calling_line_id"),
              nodeState: NodeState.UNCHECKED,
            },
            {
              ...columnData("agent.phone.outbound_phone_mode"),
              nodeState: NodeState.UNCHECKED,
            },
          ],
        },
        {
          id: ApiGroups.AGENT_PHONE_RECORDING,
          displayName: "Recording",
          isGroup: true,
          nodeState: NodeState.UNCHECKED,
          children: [
            {
              ...columnData(
                "agent.phone.recording_percentage_queued_inbound_calls"
              ),
              nodeState: NodeState.UNCHECKED,
            },
            {
              ...columnData(
                "agent.phone.recording_percentage_direct_inbound_calls"
              ),
              nodeState: NodeState.UNCHECKED,
            },
            {
              ...columnData("agent.phone.recording_percentage_outbound_calls"),
              nodeState: NodeState.UNCHECKED,
            },
            {
              ...columnData("agent.phone.recording_controls"),
              nodeState: NodeState.UNCHECKED,
            },
          ],
        },
      ],
    },
    {
      id: ApiGroups.AGENT_UI,
      displayName: "Agent - UI",
      isGroup: true,
      nodeState: NodeState.UNCHECKED,
      children: [
        {
          ...columnData("agent.ui.modify_queue_assignments_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.ui.options_menu_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.ui.click_to_dial_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.ui.notification_sound"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.ui.chat_message_browser_notification_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.ui.chat_message_sound"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.ui.status_code_list_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
        {
          ...columnData("agent.ui.display_customer_cli_enabled"),
          nodeState: NodeState.UNCHECKED,
        },
      ],
    },
  ])
);
